import React, { ChangeEvent, FC, Fragment, useState } from 'react';
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, styled, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OrderFormFilters } from 'models/order-form';
import UploadIcon from '@mui/icons-material/Upload';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import ScanMobileDialog from './scan-mobile-dialog.component';
import { useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { LoadingButton } from '@mui/lab';
import { addOrderFormTemplate } from 'apollo/templates/order-form';

type OrderFormsPageActionsProps = {
    onFilterChange: (filters: OrderFormFilters) => void;
    onAddOrderForm?: () => void;
};

const OrderFormsPageActions: FC<OrderFormsPageActionsProps> = ({
    onFilterChange,
    onAddOrderForm,
}: OrderFormsPageActionsProps) => {
    const { t: translate } = useTranslation(['order-forms', 'common']);
    const [status, setStatus] = useState('NEW');
    const [isScanMobileDialogOpen, setIsScanMobileDialogOpen] = useState(false);
    const [search, setSearch] = useState('');
    const { showBoundary } = useDenthubErrorBoundary();

    const handleStatusChange = (status: string) => {
        setStatus(status);
        onFilterChange({
            status: status !== 'NEW' ? status : undefined,
            search: search.length > 2 ? search : undefined,
        });
    };

    const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value);

        onFilterChange({
            status: status !== 'NEW' ? status : undefined,
            search: event.target.value.length > 2 ? event.target.value : undefined,
        });
    };

    const [addOrderForm, { loading }] = useMutation(addOrderFormTemplate, {
        onCompleted: () => {
            if (onAddOrderForm) {
                onAddOrderForm();
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length === 1) {
            const reader = new FileReader();
            reader.onload = () => {
                addOrderForm({ variables: { imageBase64: reader.result } });
            };

            reader.readAsDataURL(files[0]);
        }
    };

    return (
        <Fragment>
            <Grid container rowSpacing={1.5} columnSpacing={1}>
                <Grid item md={2} sm={2} xs={12}>
                    <FormControl fullWidth>
                        <InputLabel id="status-select-label">{translate('common:STATUS')}</InputLabel>
                        <Select
                            labelId="status-select-label"
                            label={translate('common:STATUS')}
                            value={status}
                            onChange={(e) => handleStatusChange(e.target.value)}
                        >
                            <MenuItem value="NEW">{translate('order-forms:NEW_ORDER_FORMS')}</MenuItem>
                            <MenuItem value="ORDER_CREATED">{translate('order-forms:ORDER_CREATED')}</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item md={3} sm={3} xs={12}>
                    <TextField
                        fullWidth
                        onChange={handleSearchChange}
                        label={translate('order-forms:ORDER_FORM_FREE_TEXT_FILTER')}
                        placeholder={translate('order-forms:ORDER_FORM_FREE_TEXT_FILTER_PLACEHOLDER')}
                        value={search}
                        autoComplete="off"
                    />
                </Grid>
                <Grid item sm={1} xs={false}></Grid>
                <Grid item sm={6} xs={12}>
                    <Box display="flex" justifyContent="right">
                        <LoadingButton
                            sx={{ marginRight: 1 }}
                            component="label"
                            role={undefined}
                            variant="text"
                            startIcon={<UploadIcon />}
                            loading={loading}
                        >
                            {translate('order-forms:UPLOAD_ORDER_FORM')}
                            <VisuallyHiddenInput type="file" accept="image/*" onChange={handleFileUpload} />
                        </LoadingButton>
                        <Button
                            startIcon={<PhoneAndroidIcon />}
                            variant="contained"
                            onClick={() => setIsScanMobileDialogOpen(true)}
                        >
                            {translate('order-forms:SCAN_USING_PHONE')}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
            <ScanMobileDialog open={isScanMobileDialogOpen} onClose={() => setIsScanMobileDialogOpen(false)} />
        </Fragment>
    );
};

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

export default OrderFormsPageActions;
